import React, { useState, useRef, useEffect } from 'react';
import Cropper from 'react-cropper';

const extractImageColors = (src) => {
    return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = 50;
            canvas.height = 50;
            ctx.drawImage(img, 0, 0, 50, 50);

            const dataTop = ctx.getImageData(0, 0, 50, 25).data;
            let r1 = 0, g1 = 0, b1 = 0, count1 = 0;
            for (let i = 0; i < dataTop.length; i += 4) {
                r1 += dataTop[i]; g1 += dataTop[i + 1]; b1 += dataTop[i + 2]; count1++;
            }
            r1 = Math.round(r1 / count1); g1 = Math.round(g1 / count1); b1 = Math.round(b1 / count1);

            const dataBottom = ctx.getImageData(0, 25, 50, 25).data;
            let r2 = 0, g2 = 0, b2 = 0, count2 = 0;
            for (let i = 0; i < dataBottom.length; i += 4) {
                r2 += dataBottom[i]; g2 += dataBottom[i + 1]; b2 += dataBottom[i + 2]; count2++;
            }
            r2 = Math.round(r2 / count2); g2 = Math.round(g2 / count2); b2 = Math.round(b2 / count2);

            resolve({
                color1: `rgb(${r1}, ${g1}, ${b1})`,
                color2: `rgb(${r2}, ${g2}, ${b2})`,
                gradient: `linear-gradient(135deg, rgb(${r1}, ${g1}, ${b1}), rgb(${r2}, ${g2}, ${b2}))`
            });
        };
        img.onerror = () => {
            resolve({
                color1: '#1e293b', color2: '#0f172a',
                gradient: 'linear-gradient(135deg, #1e293b, #0f172a)'
            });
        };
        img.src = src;
    });
};

export default function BoutiqueForm() {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        logo: null
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);

    // Image Upload & Crop State
    const [imagePreview, setImagePreview] = useState(null); // Before crop
    const [showCropper, setShowCropper] = useState(false);
    const cropperRef = useRef(null);
    const fileInputRef = useRef(null);

    // Cropper Pro Max UI states
    const [imageBgGradient, setImageBgGradient] = useState(null);
    const [imageColors, setImageColors] = useState(null);

    useEffect(() => {
        if (imagePreview) {
            extractImageColors(imagePreview).then(res => {
                setImageBgGradient(res.gradient);
                setImageColors({ color1: res.color1, color2: res.color2 });
            });
        } else {
            setImageBgGradient(null);
            setImageColors(null);
        }
    }, [imagePreview]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = () => {
                setImagePreview(reader.result);
                setShowCropper(true);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleCropCancel = () => {
        setShowCropper(false);
        setImagePreview(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleCropExec = async () => {
        if (cropperRef.current && cropperRef.current.cropper) {
            const sourceCanvas = cropperRef.current.cropper.getCroppedCanvas({ width: 500, height: 500 });
            if (sourceCanvas) {
                const finalCanvas = document.createElement('canvas');
                finalCanvas.width = 500;
                finalCanvas.height = 500;
                const ctx = finalCanvas.getContext('2d');

                // Fill background
                if (imageColors) {
                    const grad = ctx.createLinearGradient(0, 0, finalCanvas.width, finalCanvas.height);
                    grad.addColorStop(0, imageColors.color1);
                    grad.addColorStop(1, imageColors.color2);
                    ctx.fillStyle = grad;
                } else {
                    ctx.fillStyle = '#1e293b';
                }
                ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);

                // Draw cropped image
                ctx.drawImage(sourceCanvas, 0, 0);

                const dataUrl = finalCanvas.toDataURL('image/jpeg', 0.9);
                setFormData(prev => ({ ...prev, logo: dataUrl }));
                setShowCropper(false);
            }
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const removeLogo = () => {
        setFormData(prev => ({ ...prev, logo: null }));
        setImagePreview(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError(null);

        try {
            const response = await fetch('/api-boutique/create', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData)
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Une erreur est survenue');
            }

            if (data.success && data.redirect) {
                window.location.href = data.redirect;
            }
        } catch (err) {
            setError(err.message);
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        window.location.href = '/my-account/boutique';
    };

    return (
        <div style={{ fontFamily: "'Nunito Sans', sans-serif" }}>
            {showCropper && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ background: '#fff', borderRadius: '12px', width: '90%', maxWidth: '800px', overflow: 'hidden' }}>
                        <div style={{ padding: '15px 20px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h4 style={{ margin: 0, fontFamily: "'Rubik', sans-serif" }}>Recadrer l'image</h4>
                            <button onClick={handleCropCancel} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>&times;</button>
                        </div>
                        <div style={{ background: imageBgGradient || '#1e293b' }}>
                            <Cropper
                                src={imagePreview}
                                style={{ height: 450, width: '100%' }}
                                aspectRatio={4 / 3}
                                guides={true}
                                ref={cropperRef}
                                viewMode={0}
                                background={false}
                                responsive={true}
                                autoCropArea={0.8}
                                checkOrientation={false}
                                zoom={(e) => {
                                    const cropper = cropperRef.current?.cropper;
                                    if (!cropper) return;
                                    const cropBoxData = cropper.getCropBoxData();
                                    const canvasData = cropper.getCanvasData();
                                    if (!cropBoxData || !canvasData || !canvasData.naturalWidth || !canvasData.naturalHeight) return;

                                    const minRatioW = (cropBoxData.width / 1.2) / canvasData.naturalWidth;
                                    const minRatioH = (cropBoxData.height / 1.2) / canvasData.naturalHeight;
                                    const minRatio = Math.min(minRatioW, minRatioH);

                                    if (e.detail.ratio < minRatio) {
                                        e.preventDefault();
                                    }
                                }}
                            />
                        </div>
                        <div style={{ padding: '15px 20px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                            <button onClick={handleCropCancel} className="btn btn-light" style={{ borderRadius: '50px', padding: '8px 20px' }}>Annuler</button>
                            <button onClick={handleCropExec} className="btn btn-primary" style={{ borderRadius: '50px', padding: '8px 20px', backgroundColor: '#15803D', borderColor: '#15803D' }}>Valider le recadrage</button>
                        </div>
                    </div>
                </div>
            )}

            <form onSubmit={handleSubmit}>
                {error && (
                    <div className="alert alert-danger" role="alert" style={{ borderRadius: '8px' }}>
                        {error}
                    </div>
                )}

                <div className="mb-4 form-group" style={{ position: 'relative' }}>
                    <label className="form-label" style={{ fontWeight: 600, color: '#374151' }}>Logo de la boutique</label>

                    {!formData.logo ? (
                        <div
                            style={{
                                border: '2px dashed #d1d5db',
                                borderRadius: '12px',
                                padding: '40px 20px',
                                textAlign: 'center',
                                cursor: 'pointer',
                                background: '#f9fafb',
                                transition: 'all 0.3s'
                            }}
                            onClick={() => fileInputRef.current.click()}
                            onMouseOver={(e) => e.currentTarget.style.borderColor = '#15803D'}
                            onMouseOut={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                        >
                            <i className="fas fa-image" style={{ fontSize: '32px', color: '#9ca3af', marginBottom: '10px' }}></i>
                            <p style={{ margin: 0, color: '#6b7280', fontWeight: 500 }}>Cliquez pour ajouter une image</p>
                        </div>
                    ) : (
                        <div style={{ position: 'relative', borderRadius: '12px', padding: 3, overflow: 'hidden', border: '1px solid #eee' }}>
                            <img src={formData.logo} alt="Preview" style={{ width: '100px', borderRadius: '50%', height: 'auto', display: 'block', objectFit: 'cover' }} />
                            <button
                                type="button"
                                onClick={removeLogo}
                                style={{ position: 'absolute', top: '10px', right: '10px', background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: '50%', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 5px rgba(0,0,0,0.2)' }}
                            >
                                <i className="fas fa-trash text-danger"></i>
                            </button>
                        </div>
                    )}
                    <input
                        type="file"
                        accept="image/*"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                    />
                </div>

                <div className="mb-4 form-group" style={{ position: 'relative' }}>
                    <label htmlFor="name" className="form-label" style={{ fontWeight: 600, color: '#374151' }}>Nom de la boutique *</label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        className={`form-control ${formData.name.length > 0 ? (formData.name.length >= 2 ? 'is-valid' : 'is-invalid') : ''}`}
                        value={formData.name}
                        onChange={handleChange}
                        required
                        minLength={2}
                        maxLength={50}
                        style={{ padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db' }}
                        placeholder="Ex: Ma Super Boutique"
                    />
                    <span className="char-counter" style={{ fontSize: '0.7rem', color: '#888', position: 'absolute', right: '10px', bottom: '-22px', fontWeight: 600 }}>
                        {formData.name.length} / 50
                    </span>
                    {formData.name.length > 0 && formData.name.length < 2 && (
                        <div className="validation-feedback text-danger" style={{ fontSize: '0.75rem', position: 'absolute', left: '10px', bottom: '-22px' }}>Le nom doit faire au moins 2 caractères.</div>
                    )}
                </div>

                <div className="mb-4 form-group" style={{ position: 'relative', marginTop: '20px' }}>
                    <label htmlFor="description" className="form-label" style={{ fontWeight: 600, color: '#374151' }}>Description</label>
                    <textarea
                        id="description"
                        name="description"
                        className="form-control"
                        value={formData.description}
                        onChange={handleChange}
                        rows="4"
                        maxLength={500}
                        style={{ padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db' }}
                        placeholder="Décrivez votre boutique..."
                    ></textarea>
                    <span className="char-counter" style={{ fontSize: '0.7rem', color: '#888', position: 'absolute', right: '10px', bottom: '-22px', fontWeight: 600 }}>
                        {formData.description.length} / 500
                    </span>
                </div>

                <div className="d-flex justify-content-between mt-5 gap-3" style={{ gap: '15px' }}>
                    <button
                        type="button"
                        onClick={handleCancel}
                        className="btn btn-outline-secondary"
                        style={{
                            padding: '12px 30px',
                            borderRadius: '50px',
                            fontWeight: 600,
                            flex: 1,
                            transition: 'all 0.3s ease'
                        }}
                    >
                        Annuler / Retour
                    </button>
                    <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={isSubmitting || formData.name.length < 2}
                        style={{
                            backgroundColor: '#15803D',
                            borderColor: '#15803D',
                            padding: '12px 30px',
                            borderRadius: '50px',
                            fontWeight: 600,
                            flex: 1,
                            transition: 'all 0.3s ease'
                        }}
                    >
                        {isSubmitting ? 'Création en cours...' : 'Créer ma boutique'}
                    </button>
                </div>
            </form>
        </div>
    );
}
